import { type ReactNode } from 'react';
import { type Table } from '@tanstack/react-table';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTableSearch } from './DataTableSearch';
import { DataTableFacetedFilter } from './DataTableFacetedFilter';
import { DataTableViewOptions } from './DataTableViewOptions';
import { DataTableExport } from './DataTableExport';
import { type FacetedFilterOption } from './types';
import { cn } from '@/lib/utils';

interface FacetedFilterConfig {
  columnId: string;
  title: string;
  options: FacetedFilterOption[];
}

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  searchable?: boolean;
  toolbar?: ReactNode;
  searchPlaceholder?: string;
  facetedFilters?: FacetedFilterConfig[];
  columnToggle?: boolean;
  exportable?: boolean;
  exportFilename?: string;
  children?: ReactNode;
}

export function DataTableToolbar<TData>({
  table,
  searchable = false,
  toolbar,
  searchPlaceholder,
  facetedFilters,
  columnToggle = false,
  exportable = false,
  exportFilename,
  children,
}: DataTableToolbarProps<TData>) {
  const isFiltered = table.getState().columnFilters.length > 0;
  const hasGlobalFilter = table.getState().globalFilter;
  const showToolbar =
    searchable || facetedFilters?.length || columnToggle || exportable || children || toolbar;

  if (!showToolbar) return null;

  const hasLeftContent = Boolean(toolbar || searchable || facetedFilters?.length || children);

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 bg-card px-4',
        // A row holding nothing but two right-aligned buttons does not need
        // full height. Where the page passes filters in, it does.
        hasLeftContent ? 'py-3' : 'py-2'
      )}
    >
      <div className="flex flex-1 items-center gap-2">
        {toolbar}
        {searchable && <DataTableSearch table={table} placeholder={searchPlaceholder} />}
        {facetedFilters?.map((filter) => {
          const column = table.getColumn(filter.columnId);
          if (!column) return null;
          return (
            <DataTableFacetedFilter
              key={filter.columnId}
              column={column}
              title={filter.title}
              options={filter.options}
            />
          );
        })}
        {(isFiltered || hasGlobalFilter) && (
          <Button
            variant="ghost"
            onClick={() => {
              table.resetColumnFilters();
              table.setGlobalFilter('');
            }}
            className="h-9 px-2 lg:px-3"
          >
            Reset
            <X className="ml-2 h-4 w-4" />
          </Button>
        )}
        {children}
      </div>
      <div className="flex items-center space-x-2">
        {exportable && <DataTableExport table={table} filename={exportFilename} />}
        {columnToggle && <DataTableViewOptions table={table} />}
      </div>
    </div>
  );
}
